import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { ChevronRight, RotateCw } from 'lucide-react';
import { sbg } from '../../theme/sbgTokens';
import {
  EmptyState,
  ErrorState,
  KpiGrid,
  KpiTile,
  Meter,
  PersonCell,
  SearchField,
  SectionCard,
  SegmentedControl,
  Sheet,
  SpecGrid,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
  useDelayedFlag,
} from '../ui';
import { PageError } from '../common/PageState';
import { MISSING, formatCount, formatDate, formatDateRange, formatNumber, formatPercent, formatTime } from '../../utils/format';
import { addDaysToYmd, getTodayEt } from '../../utils/easternTime';
import {
  TeamCheckinStatus,
  TeamCheckinRepStats,
  useGetTeamCheckinAnalyticsQuery,
  useGetTeamCheckinDayQuery,
  useGetTeamCheckinHistoryQuery,
} from '../../store/slices/teamAttendanceSlice';
import {
  DateStepper,
  STALLED_BODY,
  SectionError,
  SectionLoading,
  SectionStalled,
  TableTitle,
  TeamSection,
  TextSkeleton,
  ToolbarNote,
  freshnessLabel,
  getCheckinStatusTone,
  matchesRepSearch,
  noMatchTitle,
  nowrapSegmentsSx,
  reloadPage,
  repCellSx,
  sentenceCaseLabel,
  spansSeveralTeams,
  stickyFirstColumnSx,
  useReportMeta,
  useStalled,
} from './attendanceUi';
import { ScopeNote, describeQueryError, isAccessError, scopeMeta } from './TeamScopeNote';

const WINDOW_OPTIONS = [14, 30, 60, 90];

/** Long rep lists (an admin sees every rep) scroll inside their card under a sticky header. */
const LONG_TABLE_MAX_HEIGHT = 'min(640px, 75vh)';

// Problems first, so a manager sees who needs follow-up without scanning.
const STATUS_ORDER: Record<TeamCheckinStatus, number> = {
  no_click: 0,
  offsite_click: 1,
  late_on_site: 2,
  pending: 3,
  email_unavailable: 4,
  on_time: 5,
  not_expected: 6,
  no_record: 7,
};

/** One vocabulary for a status across the pills, KPI captions and trend headers. */
const STATUS_LABEL: Record<TeamCheckinStatus, string> = {
  no_click: 'No check-in',
  offsite_click: 'Offsite check-in',
  late_on_site: 'Late',
  pending: 'Pending',
  email_unavailable: 'Email not delivered',
  on_time: 'On time',
  not_expected: 'Excused',
  no_record: 'No record',
};

const statusLabelFor = (row: { status: TeamCheckinStatus; statusLabel: string }) =>
  STATUS_LABEL[row.status] ?? sentenceCaseLabel(row.statusLabel);

/** The statuses a manager follows up on: no check-in, an offsite check-in, or a late arrival. */
const FOLLOW_UP_STATUSES = new Set<TeamCheckinStatus>(['no_click', 'offsite_click', 'late_on_site']);

type DayFilter = 'all' | 'follow_up';

const formatRank = (rank: number | null, peerCount: number) => (rank ? `#${rank} of ${peerCount}` : 'Not ranked');

/** The ET clock time a rep checked in, from the timestamp when there is one. */
const checkedInAt = (row: { clickedAt: string | null; clickedAtLabel: string | null }) =>
  row.clickedAt ? formatTime(row.clickedAt) : row.clickedAtLabel || MISSING;

/* ------------------------------------------------------------------------ */
/* One rep's history, in a sheet so the team list stays in place             */
/* ------------------------------------------------------------------------ */

/** The sheet's loading state at its real layout, revealed after 300ms. */
const HistorySkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label="Loading check-in history…" sx={{ display: 'grid', gap: 3 }}>
      <span className="sbg-sr-only">Loading check-in history…</span>
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 3, visibility: visible ? 'visible' : 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3 }}>
          {[0, 1, 2].map((cell) => (
            <Box key={cell} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1 }}>
              <Skeleton variant="rounded" width="60%" height={12} />
              <Skeleton variant="rounded" width="45%" height={28} />
              <Skeleton variant="rounded" width="80%" height={12} />
            </Box>
          ))}
        </Box>
        <TableCard>
          <Table>
            <TableBody>
              <TableSkeletonRows columns={4} rows={6} delay={0} />
            </TableBody>
          </Table>
        </TableCard>
      </Box>
    </Box>
  );
};

const RepHistorySheet: React.FC<{
  rep: TeamCheckinRepStats | null;
  endDate: string;
  days: number;
  onClose: () => void;
}> = ({ rep, endDate, days, onClose }) => {
  const history = useGetTeamCheckinHistoryQuery(
    { employeeId: rep?.employeeId || '', endDate, days },
    { skip: !rep }
  );

  // Keep the last rep on screen while the sheet slides closed.
  const [lastRep, setLastRep] = useState<TeamCheckinRepStats | null>(rep);
  useEffect(() => {
    if (rep) setLastRep(rep);
  }, [rep]);
  const shownRep = rep ?? lastRep;

  // `data` can still hold the previous rep while the next one loads.
  const data = history.data && rep && history.data.employee.employeeId === rep.employeeId ? history.data : undefined;
  const name = shownRep?.name ?? 'Rep';
  const stalled = useStalled(
    Boolean(rep) && !data && Boolean(history.isLoading || history.isFetching),
    `${rep?.employeeId}:${endDate}:${days}`
  );

  let body: React.ReactNode;
  if (stalled) {
    body = (
      <ErrorState compact title={`Couldn’t load ${name}’s check-ins`} body={STALLED_BODY} retryLabel="Reload page" onRetry={reloadPage} />
    );
  } else if (history.error && !history.isFetching) {
    body = (
      <ErrorState
        compact
        title={`Couldn’t load ${name}’s check-ins`}
        body={describeQueryError(history.error)}
        onRetry={() => history.refetch()}
      />
    );
  } else if (!data) {
    body = <HistorySkeleton />;
  } else {
    const { employee, ranks } = data;
    body = (
      <>
        <SpecGrid
          minColumnWidth={150}
          items={[
            {
              label: 'On-time rate',
              value: formatPercent(employee.onTimeRate),
              caption: `${formatRank(ranks.onTimeRateRank, ranks.peerCount)} on your team`,
            },
            {
              label: 'Average check-in',
              value: employee.averageCheckinLabel || MISSING,
              caption: `${formatRank(ranks.averageCheckinRank, ranks.peerCount)} earliest`,
            },
            {
              label: 'Decided days',
              value: formatNumber(employee.expectedDays),
              caption: `${formatNumber(employee.lateCount)} late · ${formatNumber(employee.noClickCount)} no check-in · ${formatNumber(employee.offsiteCount)} offsite`,
            },
          ]}
        />
        <TableCard aria-label={`${name}’s check-in days`}>
          <Table aria-label={`${name} check-in history`}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Checked in</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.history.length === 0 ? (
                <TableEmptyRow
                  colSpan={4}
                  title="No check-ins in this window"
                  body="Days appear here once the morning check-in email goes out to this rep."
                />
              ) : (
                data.history.map((row) => (
                  <TableRow key={row.workDate}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.workDate, { weekday: true })}</TableCell>
                    <TableCell>
                      <StatusPill tone={getCheckinStatusTone(row.status)}>{statusLabelFor(row)}</StatusPill>
                    </TableCell>
                    <TableCell sx={{ color: sbg.text.secondary, minWidth: 200 }}>{row.statusDetail}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{checkedInAt(row)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  }

  return (
    <Sheet
      open={Boolean(rep)}
      onClose={onClose}
      size="lg"
      title={name}
      subtitle={`Last ${days} days through ${formatDate(endDate)}`}
      bodySx={{ gap: 3 }}
    >
      {body}
    </Sheet>
  );
};

/* ------------------------------------------------------------------------ */
/* The panel                                                                 */
/* ------------------------------------------------------------------------ */

const TeamCheckinPanel: React.FC<{
  /** Receives the header meta (scope · day · freshness) for the page header. */
  onMetaChange?: (parts: string[]) => void;
}> = ({ onMetaChange }) => {
  const today = useMemo(() => getTodayEt(), []);
  const [workDate, setWorkDate] = useState(today);
  const [days, setDays] = useState(30);
  const [historyRep, setHistoryRep] = useState<TeamCheckinRepStats | null>(null);
  const [search, setSearch] = useState('');
  const [dayFilter, setDayFilter] = useState<DayFilter>('all');

  const day = useGetTeamCheckinDayQuery({ workDate });
  const analytics = useGetTeamCheckinAnalyticsQuery({ endDate: workDate, days });

  // Each section loads, fails and refreshes on its own, so one slow source never hides the other.
  const dayStalled = useStalled(Boolean(day.isLoading || day.isFetching), workDate);
  const trendStalled = useStalled(Boolean(analytics.isLoading || analytics.isFetching), `${workDate}:${days}`);

  const dayReps = useMemo(
    () => [...(day.data?.reps || [])].sort((left, right) =>
      STATUS_ORDER[left.status] - STATUS_ORDER[right.status] || left.name.localeCompare(right.name)
    ),
    [day.data]
  );
  // Lowest on-time rate first; reps with no decided days go last.
  const trendReps = useMemo(
    () => [...(analytics.data?.reps || [])].sort((left, right) => {
      if (left.onTimeRate === null || right.onTimeRate === null) {
        return left.onTimeRate === null ? (right.onTimeRate === null ? left.name.localeCompare(right.name) : 1) : -1;
      }
      return left.onTimeRate - right.onTimeRate || left.name.localeCompare(right.name);
    }),
    [analytics.data]
  );

  const scope = day.data?.scope ?? analytics.data?.scope;
  const meta = [
    ...scopeMeta(scope),
    formatDate(workDate, { weekday: true }),
    freshnessLabel([day, analytics]),
  ].filter((part): part is string => Boolean(part));
  useReportMeta(onMetaChange, meta);

  // A refetch is ignored while a request is still pending, so the button only works when one can run.
  const bothPending = Boolean(day.isFetching && analytics.isFetching);
  const refreshAll = () => {
    day.refetch();
    analytics.refetch();
  };
  const searching = search.trim() !== '';
  const filtersActive = searching || dayFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setDayFilter('all');
  };
  const showFollowUp = () => setDayFilter('follow_up');

  const toolbar = (
    <Toolbar
      sx={{ alignItems: 'flex-end', rowGap: 2 }}
      end={
        <Tooltip title={bothPending ? 'Already loading the latest data' : 'Refresh data'} describeChild>
          <span>
            <IconButton aria-label="Refresh data" onClick={refreshAll} disabled={bothPending}>
              <RotateCw />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <DateStepper
        label="Day"
        value={workDate}
        max={today}
        onChange={setWorkDate}
        onPrevious={() => setWorkDate(addDaysToYmd(workDate, -1))}
        onNext={() => setWorkDate(addDaysToYmd(workDate, 1) > today ? today : addDaysToYmd(workDate, 1))}
        previousLabel="Previous day"
        nextLabel="Next day"
        nextDisabledReason={workDate >= today ? 'Today is the latest day' : null}
      />
      {workDate !== today && (
        <Button variant="text" onClick={() => setWorkDate(today)}>
          Go to today
        </Button>
      )}
      <SearchField label="Search reps" placeholder="Search reps or teams" value={search} onChange={setSearch} width={260} />
    </Toolbar>
  );

  /* ---- The day ---------------------------------------------------------- */

  let dayBody: React.ReactNode;
  if (dayStalled) {
    dayBody = <SectionStalled title="Couldn’t load the day’s check-ins" />;
  } else if (day.error && !day.isFetching) {
    dayBody = (
      <SectionError title="Couldn’t load the day’s check-ins" body={describeQueryError(day.error)} onRetry={() => day.refetch()} />
    );
  } else if (!day.data) {
    dayBody = <SectionLoading key={workDate} label="Loading team check-ins…" summary="tiles" columns={4} />;
  } else {
    const { summary } = day.data;
    const deadline = day.data.onTimeDeadlineLabel;
    const missed = summary.noClick + summary.offsite;
    const allWithoutRecords = summary.noRecord === summary.teamSize;
    const showDayTeam = spansSeveralTeams(dayReps);
    const visibleDayReps = dayReps.filter(
      (rep) => matchesRepSearch(rep, search) && (dayFilter === 'all' || FOLLOW_UP_STATUSES.has(rep.status))
    );
    const followUpCount = dayReps.filter((rep) => FOLLOW_UP_STATUSES.has(rep.status)).length;
    const dayColumns = showDayTeam ? 5 : 4;

    dayBody = (
      <>
        <KpiGrid phoneColumns={2} sx={{ mb: 0 }}>
          <KpiTile
            label="Expected"
            value={formatNumber(summary.expected)}
            caption={summary.pending > 0 ? `${formatNumber(summary.pending)} awaiting check-in` : `${formatNumber(summary.notExpected)} excused`}
            info="Reps due at the office this day. Excused reps are not expected."
          />
          <KpiTile label="On time" value={formatNumber(summary.onTime)} caption={`of ${formatNumber(summary.expected)} expected`} />
          <KpiTile
            label="Late"
            value={formatNumber(summary.late)}
            caption={`Checked in after ${deadline}`}
            onClick={showFollowUp}
            aria-label={`Late: ${formatNumber(summary.late)}. Show reps who need follow-up`}
          />
          <KpiTile
            label="Missed"
            value={formatNumber(missed)}
            caption={`${formatNumber(summary.noClick)} no check-in · ${formatNumber(summary.offsite)} offsite`}
            onClick={showFollowUp}
            aria-label={`Missed: ${formatNumber(missed)}. Show reps who need follow-up`}
          />
        </KpiGrid>

        <TableCard
          aria-label="Check-ins for the day"
          maxHeight={LONG_TABLE_MAX_HEIGHT}
          toolbar={
            <Toolbar end={<ToolbarNote>{formatCount(visibleDayReps.length, 'rep')}</ToolbarNote>}>
              <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <SegmentedControl<DayFilter>
                  size="small"
                  aria-label="Show reps"
                  value={dayFilter}
                  onChange={setDayFilter}
                  sx={nowrapSegmentsSx}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'follow_up', label: `Needs follow-up (${formatNumber(followUpCount)})` },
                  ]}
                />
              </Box>
              {allWithoutRecords && (
                <ToolbarNote>No check-in records this day. Weekends, holidays and mornings before the email goes out have none.</ToolbarNote>
              )}
            </Toolbar>
          }
        >
          <Table stickyHeader aria-label="Team check-in status" sx={stickyFirstColumnSx}>
            <TableHead>
              <TableRow>
                <TableCell>Rep</TableCell>
                {showDayTeam && <TableCell>Team</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Checked in</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDayReps.length === 0 ? (
                <TableEmptyRow
                  colSpan={dayColumns}
                  title={searching ? noMatchTitle(search) : dayFilter === 'follow_up' ? 'No one needs follow-up' : 'No reps this day'}
                  body={
                    searching
                      ? 'Try a different name or team, or show every rep.'
                      : dayFilter === 'follow_up'
                        ? 'Every expected rep checked in on time from the office, or is still inside the window.'
                        : 'Reps appear here once they are on your team.'
                  }
                  action={
                    filtersActive ? (
                      <Button variant="outlined" size="small" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                visibleDayReps.map((rep) => (
                  <TableRow key={rep.employeeId}>
                    <TableCell sx={repCellSx}>
                      <PersonCell name={rep.name} size={24} />
                    </TableCell>
                    {showDayTeam && <TableCell sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>{rep.teamName || MISSING}</TableCell>}
                    <TableCell>
                      <StatusPill tone={getCheckinStatusTone(rep.status)}>{statusLabelFor(rep)}</StatusPill>
                    </TableCell>
                    <TableCell sx={{ color: sbg.text.secondary, minWidth: 240 }}>{rep.statusDetail}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{checkedInAt(rep)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  }

  const dayDescription = day.data
    ? day.data.isToday && day.data.stage === 'before_cutoff'
      ? `Check-ins are open until ${day.data.onTimeDeadlineLabel}. Reps who haven’t checked in yet show as pending.`
      : `On time means an office check-in by ${day.data.onTimeDeadlineLabel}.`
    : day.error || dayStalled
      ? undefined
      : <TextSkeleton width={300} />;

  /* ---- The trend -------------------------------------------------------- */

  // The window the controls ask for; the server's range when its answer is for these controls.
  const trendCurrent = analytics.data && analytics.data.days === days && analytics.data.endDate === workDate;
  const trendRange = trendCurrent && analytics.data
    ? formatDateRange(analytics.data.startDate, analytics.data.endDate)
    : formatDateRange(addDaysToYmd(workDate, -(days - 1)), workDate);

  let trendBody: React.ReactNode;
  if (trendStalled) {
    trendBody = <SectionStalled title="Couldn’t load check-in trends" />;
  } else if (analytics.error && !analytics.isFetching) {
    trendBody = (
      <SectionError title="Couldn’t load check-in trends" body={describeQueryError(analytics.error)} onRetry={() => analytics.refetch()} />
    );
  } else if (!analytics.data) {
    trendBody = <SectionLoading key={`${workDate}:${days}`} label="Loading check-in trends…" summary="spec" columns={9} />;
  } else {
    const totals = analytics.data.totals;
    const showTrendTeam = spansSeveralTeams(trendReps);
    const visibleTrendReps = trendReps.filter((rep) => matchesRepSearch(rep, search));
    const trendColumns = showTrendTeam ? 10 : 9;

    trendBody = (
      <>
        <SpecGrid
          items={[
            {
              label: 'On-time rate',
              value: formatPercent(totals.onTimeRate),
              caption: `${formatNumber(totals.onTimeCount)} of ${formatCount(totals.expectedDays, 'decided day')}`,
            },
            { label: 'Issue rate', value: formatPercent(totals.issueRate), caption: 'Late, no check-in, or offsite check-in' },
            {
              label: 'Late arrivals',
              value: formatNumber(totals.lateCount),
              caption: `${formatPercent(totals.lateRate)} of decided days`,
            },
            {
              label: 'Missed days',
              value: formatNumber(totals.noClickCount + totals.offsiteCount),
              caption: `${formatNumber(totals.noClickCount)} no check-in · ${formatNumber(totals.offsiteCount)} offsite`,
            },
          ]}
        />

        <TableCard
          aria-label="Check-in trends by rep"
          maxHeight={LONG_TABLE_MAX_HEIGHT}
          toolbar={
            <Toolbar end={<ToolbarNote>Lowest on-time rate first</ToolbarNote>}>
              <TableTitle caption={formatCount(visibleTrendReps.length, 'rep')}>By rep</TableTitle>
            </Toolbar>
          }
        >
          <Table stickyHeader aria-label="Team check-in trends" sx={stickyFirstColumnSx}>
            <TableHead>
              <TableRow>
                <TableCell>Rep</TableCell>
                {showTrendTeam && <TableCell>Team</TableCell>}
                <TableCell align="right">On-time rate</TableCell>
                <TableCell align="right">Average check-in</TableCell>
                <TableCell align="right">Decided days</TableCell>
                <TableCell align="right">On time</TableCell>
                <TableCell align="right">Late</TableCell>
                <TableCell align="right">No check-in</TableCell>
                <TableCell align="right">Offsite</TableCell>
                <TableCell>
                  <span className="sbg-sr-only">History</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTrendReps.length === 0 ? (
                <TableEmptyRow
                  colSpan={trendColumns}
                  title={searching ? noMatchTitle(search) : 'No check-in days in this window'}
                  body={searching ? 'Try a different name or team.' : 'Rates appear once reps have decided check-in days.'}
                  action={
                    searching ? (
                      <Button variant="outlined" size="small" onClick={() => setSearch('')}>
                        Clear search
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                visibleTrendReps.map((rep) => (
                  <TableRow key={rep.employeeId}>
                    <TableCell sx={repCellSx}>
                      <PersonCell name={rep.name} size={24} />
                    </TableCell>
                    {showTrendTeam && <TableCell sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>{rep.teamName || MISSING}</TableCell>}
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25 }}>
                        {rep.onTimeRate !== null && (
                          <Box aria-hidden="true" sx={{ width: 56, display: { xs: 'none', md: 'block' } }}>
                            <Meter value={rep.onTimeRate} max={100} />
                          </Box>
                        )}
                        <Box component="span" sx={{ minWidth: 40, typography: 'labelMd', color: sbg.text.primary }}>
                          {formatPercent(rep.onTimeRate)}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{rep.averageCheckinLabel || MISSING}</TableCell>
                    <TableCell align="right">{formatNumber(rep.expectedDays)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.onTimeCount)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.lateCount)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.noClickCount)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.offsiteCount)}</TableCell>
                    <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap', py: 0.5 }}>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<ChevronRight size={14} />}
                        onClick={() => setHistoryRep(rep)}
                        aria-label={`View history for ${rep.name}`}
                      >
                        View history
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  }

  /* ---- The panel -------------------------------------------------------- */

  const accessError = [day, analytics].find((query) => isAccessError(query.error) && !query.isFetching)?.error;

  let content: React.ReactNode;
  if (accessError) {
    content = <PageError title="Couldn’t load team check-ins" description={describeQueryError(accessError)} onRetry={refreshAll} />;
  } else if (scope && scope.teamSize === 0) {
    content = (
      <>
        <ScopeNote scope={scope} />
        <SectionCard>
          <EmptyState
            title="No reps on your team yet"
            body="Reps you manage appear here once an admin links them to you in their employee record."
          />
        </SectionCard>
      </>
    );
  } else {
    content = (
      <>
        <ScopeNote scope={scope} />

        <TeamSection
          id="team-checkin-day"
          title={workDate === today ? 'Today' : formatDate(workDate, { weekday: true })}
          description={dayDescription}
          refreshing={Boolean(day.data) && day.isFetching && !dayStalled}
          refreshLabel="Refreshing the day’s check-ins"
        >
          {dayBody}
        </TeamSection>

        <TeamSection
          id="team-checkin-trend"
          title={`Last ${days} days`}
          description={`${trendRange}. Days before their check-in cutoff aren’t counted yet; excused days and undelivered emails never count against a rep.`}
          refreshing={Boolean(analytics.data) && analytics.isFetching && !trendStalled}
          refreshLabel="Refreshing check-in trends"
          action={
            <SegmentedControl
              size="small"
              aria-label="Trend window"
              value={String(days)}
              onChange={(value) => setDays(Number(value))}
              sx={nowrapSegmentsSx}
              options={WINDOW_OPTIONS.map((option) => ({ value: String(option), label: `${option} days` }))}
            />
          }
        >
          {trendBody}
        </TeamSection>
      </>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      {toolbar}
      <Box sx={{ display: 'grid', gap: 5, minWidth: 0 }}>{content}</Box>
      <RepHistorySheet rep={historyRep} endDate={workDate} days={days} onClose={() => setHistoryRep(null)} />
    </Box>
  );
};

export default TeamCheckinPanel;
